import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import {
  getPayrollSettings,
  updatePayrollSettings,
  createRateLevel,
  updateRateLevel,
  deleteRateLevel,
  updateStaffPayProfile,
  updateStaffCommissionPlan,
  previewPayroll,
  createPayrollRun,
  listPayrollRuns,
  getPayrollRun,
  deletePayrollRun,
  updatePayrollRunStaffPayout,
  exportPayrollRunCsv,
} from "../api/payroll";
import ConfirmModal from "../components/modals/ConfirmModal";
import { formatDateOnlyYYYYMMDD } from "../utils/dateOnly";
import "./HrSectionCompact.css";

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateParts = (value) => {
  if (!value || typeof value !== "string") return null;
  const [yearStr, monthStr, dayStr] = value.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  return { year, month, day };
};

const addDays = (dateStr, days, timeZone) => {
  const parts = parseDateParts(dateStr);
  if (!parts) return dateStr;
  const base = new Date(parts.year, parts.month - 1, parts.day, 12, 0, 0);
  base.setDate(base.getDate() + days);
  return formatDateISO(base, timeZone);
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatMoney = (value) => {
  const num = toNumber(value);
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatWeekday = (value) => {
  if (!value) return "";
  const parts = parseDateParts(value);
  if (!parts) return "";
  const date = new Date(parts.year, parts.month - 1, parts.day, 12, 0, 0);
  return date.toLocaleDateString("en-US", { weekday: "short" });
};

const normalizeStaffProfile = (profile) => ({
  ...profile,
  hourly_level_id: profile.hourly_level_id ?? null,
  daily_level_id: profile.daily_level_id ?? null,
  hourly_rate_override:
    profile.hourly_rate_override == null ? "" : String(profile.hourly_rate_override),
  daily_rate_override:
    profile.daily_rate_override == null ? "" : String(profile.daily_rate_override),
});

const HELP_SECTIONS = [
  {
    id: "overview",
    title: "What this page does",
    description: "Configure payroll rules, preview payouts, and save payroll runs.",
    anchorId: "pr-overview",
    bullets: [
      "Choose payroll date range and run preview.",
      "Set commission tiers and pay levels.",
      "Review payroll results, then save and export.",
    ],
    tips: ["Save payroll run before exporting CSV."],
  },
  {
    id: "range-history",
    title: "Payroll range and history",
    description: "Control run dates and manage previously saved payroll runs.",
    anchorId: "pr-range",
    bullets: [
      "Start and end dates use salon timezone boundaries.",
      "Preview Payroll refreshes all calculations.",
      "History lets you reload or delete a run.",
    ],
    tips: ["Use full pay-period dates for consistent results."],
  },
  {
    id: "rules",
    title: "Commission and rate rules",
    description: "Set default commission tiers and hourly/daily rate levels.",
    anchorId: "pr-commission",
    bullets: [
      "Basis controls whether tiers use service, retail, or total.",
      "Add and sort rate levels before assigning to staff.",
    ],
    tips: ["Review tier cutoffs when changing commission basis."],
  },
  {
    id: "staff-setup",
    title: "Employee pay setup",
    description: "Assign pay mode, levels, and overrides per employee.",
    anchorId: "pr-staff",
    bullets: [
      "Enable custom commission only when staff needs custom tiers.",
      "Set overrides only when different from assigned level.",
    ],
    tips: ["Use Save for each employee row after edits."],
  },
  {
    id: "results",
    title: "Payroll results and pay split",
    description: "Review calculated totals and split payout values before final save.",
    anchorId: "pr-results",
    bullets: [
      "Payroll Results shows totals by employee.",
      "Daily Breakdown shows day-level source values.",
      "Pay Split supports cheque, cash, and extra pay entries.",
    ],
    tips: ["Verify totals before clicking Save Payroll Run."],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "Preview shows no staff",
    body: "Only employees with Uses Time Clock enabled appear in payroll setup.",
  },
  {
    title: "Cannot export CSV",
    body: "Save the payroll run first, then use Export CSV.",
  },
  {
    title: "Totals look wrong",
    body: "Check date range, pay mode, and commission tier settings.",
  },
];

const TOOLTIPS = {
  start_date: "Payroll start date in salon timezone.",
  end_date: "Payroll end date in salon timezone.",
  payroll_history: "Saved payroll runs for quick reload.",
  label: "Name shown for commission payout column.",
  basis: "Sales basis used for commission tier calculations.",
  pay_mode: "How base pay is calculated for this employee.",
};

const STAFF_SETUP_TABLE_CLASS = "w-full min-w-[1280px] text-xs";
const STAFF_SETUP_HEADER_CELL_CLASS = "px-3 py-2 text-left whitespace-nowrap";
const STAFF_SETUP_SELECT_CLASS =
  "w-full min-w-[150px] rounded-lg border border-slate-200 px-2 py-1 pr-8 text-xs";
const STAFF_SETUP_LEVEL_SELECT_CLASS =
  "w-full min-w-[180px] rounded-lg border border-slate-200 px-2 py-1 pr-8 text-xs";
const STAFF_SETUP_OVERRIDE_INPUT_CLASS =
  "w-full min-w-[160px] rounded-lg border border-slate-200 px-2 py-1 pr-7 text-xs";

export default function Payroll() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const salonTimeZone = user?.salon_timezone || "America/Toronto";
  const modalText = "text-sm md:text-base";

  const [settingsLoading, setSettingsLoading] = useState(false);
  const [runsLoading, setRunsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [savingRun, setSavingRun] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [commissionLabel, setCommissionLabel] = useState("Commission");
  const [defaultBasis, setDefaultBasis] = useState("total");
  const [defaultPlansByBasis, setDefaultPlansByBasis] = useState({});
  const [staffPlansByStaff, setStaffPlansByStaff] = useState({});
  const [staffBasisById, setStaffBasisById] = useState({});

  const [rateLevels, setRateLevels] = useState([]);
  const [staffProfiles, setStaffProfiles] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState("");

  const [previewData, setPreviewData] = useState(null);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [payouts, setPayouts] = useState({});
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteRun, setConfirmDeleteRun] = useState(null);
  const [deletingRunId, setDeletingRunId] = useState(null);

  const [newHourlyLevel, setNewHourlyLevel] = useState({
    name: "",
    amount: "",
    sort_order: "",
  });
  const [newDailyLevel, setNewDailyLevel] = useState({
    name: "",
    amount: "",
    sort_order: "",
  });
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);
  const loadRunRequestIdRef = useRef(0);

  useEffect(() => {
    if (!startDate || !endDate) {
      const today = formatDateISO(new Date(), salonTimeZone);
      setStartDate(addDays(today, -6, salonTimeZone));
      setEndDate(today);
    }
  }, [salonTimeZone, startDate, endDate]);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    },
    []
  );

  const loadSettings = async () => {
    if (!token) return;
    setSettingsLoading(true);
    setError("");
    try {
      const data = await getPayrollSettings(token);
      setCommissionLabel(data?.commission_label || "Commission");
      const defaultPlans = {};
      (data?.default_commission_plans || []).forEach((plan) => {
        defaultPlans[plan.basis] = (plan.tiers || []).map((tier) => ({
          min_revenue: tier.min_revenue ?? "",
          rate_percent: tier.rate_percent ?? "",
          sort_order: tier.sort_order ?? 0,
        }));
      });
      const staffPlans = {};
      (data?.staff_commission_plans || []).forEach((plan) => {
        const staffId = plan.staff_id;
        if (!staffId) return;
        if (!staffPlans[staffId]) staffPlans[staffId] = {};
        staffPlans[staffId][plan.basis] = (plan.tiers || []).map((tier) => ({
          min_revenue: tier.min_revenue ?? "",
          rate_percent: tier.rate_percent ?? "",
          sort_order: tier.sort_order ?? 0,
        }));
      });
      setDefaultPlansByBasis(defaultPlans);
      setStaffPlansByStaff(staffPlans);
      setRateLevels(Array.isArray(data?.rate_levels) ? data.rate_levels : []);
      const normalizedProfiles = (Array.isArray(data?.staff_profiles) ? data.staff_profiles : []).map(
        normalizeStaffProfile
      );
      const filteredProfiles = normalizedProfiles.filter((profile) => Boolean(profile.uses_time_clock));
      setStaffProfiles(filteredProfiles);
      const nextStaffBasis = {};
      filteredProfiles.forEach((staff) => {
        const plan = staffPlans[staff.staff_id];
        const basis = plan ? Object.keys(plan)[0] : defaultBasis;
        nextStaffBasis[staff.staff_id] = basis || defaultBasis;
      });
      setStaffBasisById(nextStaffBasis);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load payroll settings.");
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadRuns = async () => {
    if (!token) return;
    setRunsLoading(true);
    try {
      const data = await listPayrollRuns(token);
      setPayrollRuns(Array.isArray(data) ? data : []);
    } catch (err) {
      setPayrollRuns([]);
    } finally {
      setRunsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadRuns();
  }, [token]);

  useEffect(() => {
    if (!previewData?.staff) return;
    const next = {};
    previewData.staff.forEach((row) => {
      next[row.staff_id] = {
        pay_cheque: row.pay_cheque ?? "",
        pay_cash: row.pay_cash ?? "",
        extra_pay: row.extra_pay ?? "",
        notes: row.notes ?? "",
      };
    });
    setPayouts(next);
  }, [previewData]);

  const hourlyLevels = useMemo(
    () => rateLevels.filter((level) => level.rate_type === "hourly" && level.is_active !== false),
    [rateLevels]
  );
  const dailyLevels = useMemo(
    () => rateLevels.filter((level) => level.rate_type === "daily" && level.is_active !== false),
    [rateLevels]
  );

  const defaultTiers = defaultPlansByBasis[defaultBasis] || [];

  const updateDefaultTier = (index, field, value) => {
    setDefaultPlansByBasis((prev) => {
      const next = { ...prev };
      const tiers = [...(next[defaultBasis] || [])];
      tiers[index] = { ...tiers[index], [field]: value };
      next[defaultBasis] = tiers;
      return next;
    });
  };

  const addDefaultTier = () => {
    setDefaultPlansByBasis((prev) => {
      const next = { ...prev };
      const tiers = [...(next[defaultBasis] || [])];
      tiers.push({ min_revenue: "", rate_percent: "", sort_order: tiers.length });
      next[defaultBasis] = tiers;
      return next;
    });
  };

  const removeDefaultTier = (index) => {
    setDefaultPlansByBasis((prev) => {
      const next = { ...prev };
      const tiers = [...(next[defaultBasis] || [])];
      tiers.splice(index, 1);
      next[defaultBasis] = tiers;
      return next;
    });
  };

  const updateStaffTier = (staffId, index, field, value) => {
    const basis = staffBasisById[staffId] || defaultBasis;
    setStaffPlansByStaff((prev) => {
      const next = { ...prev };
      const staffPlans = { ...(next[staffId] || {}) };
      const tiers = [...(staffPlans[basis] || [])];
      tiers[index] = { ...tiers[index], [field]: value };
      staffPlans[basis] = tiers;
      next[staffId] = staffPlans;
      return next;
    });
  };

  const addStaffTier = (staffId) => {
    const basis = staffBasisById[staffId] || defaultBasis;
    setStaffPlansByStaff((prev) => {
      const next = { ...prev };
      const staffPlans = { ...(next[staffId] || {}) };
      const tiers = [...(staffPlans[basis] || [])];
      tiers.push({ min_revenue: "", rate_percent: "", sort_order: tiers.length });
      staffPlans[basis] = tiers;
      next[staffId] = staffPlans;
      return next;
    });
  };

  const removeStaffTier = (staffId, index) => {
    const basis = staffBasisById[staffId] || defaultBasis;
    setStaffPlansByStaff((prev) => {
      const next = { ...prev };
      const staffPlans = { ...(next[staffId] || {}) };
      const tiers = [...(staffPlans[basis] || [])];
      tiers.splice(index, 1);
      staffPlans[basis] = tiers;
      next[staffId] = staffPlans;
      return next;
    });
  };

  const updateStaffProfileField = (staffId, field, value) => {
    setStaffProfiles((prev) =>
      prev.map((profile) =>
        profile.staff_id === staffId ? { ...profile, [field]: value } : profile
      )
    );
  };

  const updateRateLevelField = (levelId, field, value) => {
    setRateLevels((prev) =>
      prev.map((level) =>
        level.id === levelId ? { ...level, [field]: value } : level
      )
    );
  };

  const totalSummary = useMemo(() => {
    const rows = previewData?.staff || [];
    return rows.reduce(
      (acc, row) => {
        acc.hours += toNumber(row.hours);
        acc.days += toNumber(row.days);
        acc.service += toNumber(row.service_sales);
        acc.retail += toNumber(row.retail_sales);
        acc.totalSales += toNumber(row.total_sales);
        acc.commission += toNumber(row.commission_pay);
        acc.base += toNumber(row.base_pay);
        acc.totalPay += toNumber(row.total_pay);
        return acc;
      },
      {
        hours: 0,
        days: 0,
        service: 0,
        retail: 0,
        totalSales: 0,
        commission: 0,
        base: 0,
        totalPay: 0,
      }
    );
  }, [previewData]);

  const dayKeys = previewData?.days || [];
  const activeCommissionLabel = previewData?.commission_label || commissionLabel;
  const breakdownDetailsByStaff = useMemo(() => {
    const details = previewData?.daily_breakdown_details || [];
    return details.reduce((acc, detail) => {
      if (detail && detail.staff_id != null) {
        acc[detail.staff_id] = detail;
      }
      return acc;
    }, {});
  }, [previewData]);

  const getPayoutValue = (staffId, field) => payouts?.[staffId]?.[field] ?? "";

  const cancelPendingLoadRun = () => {
    loadRunRequestIdRef.current += 1;
    setPreviewLoading(false);
  };

  const setPayoutValue = (staffId, field, value) => {
    setPayouts((prev) => ({
      ...prev,
      [staffId]: {
        ...prev[staffId],
        [field]: value,
      },
    }));
  };

  const handleSaveSettings = async () => {
    if (!token) return;
    setNotice("");
    setError("");
    const tiers = (defaultPlansByBasis[defaultBasis] || []).map((tier, idx) => ({
      min_revenue: toNumber(tier.min_revenue),
      rate_percent: toNumber(tier.rate_percent),
      sort_order: idx,
    }));
    try {
      await updatePayrollSettings(
        {
          commission_label: commissionLabel,
          default_commission_plans: [{ basis: defaultBasis, tiers }],
        },
        token
      );
      setNotice("Payroll settings saved.");
      await loadSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save payroll settings.");
    }
  };

  const handleCreateRateLevel = async (rateType) => {
    if (!token) return;
    const payload = rateType === "hourly" ? newHourlyLevel : newDailyLevel;
    if (!payload.name) {
      setError("Level name is required.");
      return;
    }
    try {
      await createRateLevel(
        {
          rate_type: rateType,
          name: payload.name,
          amount: toNumber(payload.amount),
          sort_order: Number(payload.sort_order) || 0,
          is_active: true,
        },
        token
      );
      if (rateType === "hourly") {
        setNewHourlyLevel({ name: "", amount: "", sort_order: "" });
      } else {
        setNewDailyLevel({ name: "", amount: "", sort_order: "" });
      }
      await loadSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to add rate level.");
    }
  };

  const handleUpdateRateLevel = async (level) => {
    if (!token) return;
    try {
      await updateRateLevel(
        level.id,
        {
          name: level.name,
          amount: toNumber(level.amount),
          sort_order: Number(level.sort_order) || 0,
          is_active: level.is_active !== false,
        },
        token
      );
      await loadSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update rate level.");
    }
  };

  const handleDeleteRateLevel = async (levelId) => {
    if (!token) return;
    try {
      await deleteRateLevel(levelId, token);
      await loadSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to delete rate level.");
    }
  };

  const handleSaveStaffProfile = async (profile) => {
    if (!token) return;
    setError("");
    try {
      const hourlyLevelId =
        profile.hourly_level_id === null || profile.hourly_level_id === ""
          ? null
          : Number(profile.hourly_level_id);
      const dailyLevelId =
        profile.daily_level_id === null || profile.daily_level_id === ""
          ? null
          : Number(profile.daily_level_id);
      const payload = {
        pay_mode: profile.pay_mode,
        hourly_level_id: Number.isFinite(hourlyLevelId) ? hourlyLevelId : null,
        daily_level_id: Number.isFinite(dailyLevelId) ? dailyLevelId : null,
        hourly_rate_override: profile.hourly_rate_override === "" ? null : toNumber(profile.hourly_rate_override),
        daily_rate_override: profile.daily_rate_override === "" ? null : toNumber(profile.daily_rate_override),
        use_custom_commission: Boolean(profile.use_custom_commission),
      };
      const saved = await updateStaffPayProfile(profile.staff_id, payload, token);
      const normalized = saved ? normalizeStaffProfile(saved) : profile;
      setStaffProfiles((prev) =>
        prev.map((row) => (row.staff_id === profile.staff_id ? { ...row, ...normalized } : row))
      );
      setNotice(`Saved ${profile.staff_name}.`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save staff profile.");
    }
  };

  const handleSaveStaffCommissionPlan = async (staffId) => {
    if (!token) return;
    const basis = staffBasisById[staffId] || defaultBasis;
    const tiers = (staffPlansByStaff?.[staffId]?.[basis] || []).map((tier, idx) => ({
      min_revenue: toNumber(tier.min_revenue),
      rate_percent: toNumber(tier.rate_percent),
      sort_order: idx,
    }));
    try {
      await updateStaffCommissionPlan(
        staffId,
        basis,
        { basis, tiers },
        token
      );
      setNotice("Custom commission tiers saved.");
      await loadSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save custom tiers.");
    }
  };

  const handlePreview = async () => {
    if (!token) return;
    cancelPendingLoadRun();
    setDateError("");
    setError("");
    setNotice("");
    if (!startDate || !endDate) {
      setDateError("Start and end dates are required.");
      return;
    }
    if (endDate < startDate) {
      setDateError("End date cannot be earlier than start date.");
      return;
    }
    setPreviewLoading(true);
    try {
      const data = await previewPayroll(
        { start: startDate, end: endDate },
        token
      );
      setPreviewData(data);
      setCurrentRunId(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to preview payroll.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleLoadRun = async (runId) => {
    if (!token) return;
    const requestId = loadRunRequestIdRef.current + 1;
    loadRunRequestIdRef.current = requestId;
    setPreviewLoading(true);
    setError("");
    setNotice("");
    try {
      const data = await getPayrollRun(runId, token);
      if (loadRunRequestIdRef.current !== requestId) return;
      setPreviewData(data);
      setCurrentRunId(runId);
    } catch (err) {
      if (loadRunRequestIdRef.current !== requestId) return;
      setError(err?.response?.data?.detail || "Unable to load payroll run.");
    } finally {
      if (loadRunRequestIdRef.current === requestId) {
        setPreviewLoading(false);
      }
    }
  };

  const handleDeleteRun = async () => {
    if (!token || !confirmDeleteRun) return;
    const runId = confirmDeleteRun.id || confirmDeleteRun;
    if (!runId) return;
    cancelPendingLoadRun();
    setDeletingRunId(runId);
    setError("");
    setNotice("");
    try {
      await deletePayrollRun(runId, token);
      await loadRuns();
      if (currentRunId === runId) {
        setCurrentRunId(null);
        setPreviewData(null);
        setPayouts({});
      }
      setNotice("Payroll run deleted.");
      setConfirmDeleteOpen(false);
      setConfirmDeleteRun(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to delete payroll run.");
    } finally {
      setDeletingRunId(null);
    }
  };

  const handleSaveRun = async () => {
    if (!previewData || !token) return;
    cancelPendingLoadRun();
    setSavingRun(true);
    setError("");
    setNotice("");
    try {
      if (!currentRunId) {
        const staffPayouts = (previewData.staff || []).map((row) => ({
          staff_id: row.staff_id,
          pay_cheque: toNumber(getPayoutValue(row.staff_id, "pay_cheque")),
          pay_cash: toNumber(getPayoutValue(row.staff_id, "pay_cash")),
          extra_pay: toNumber(getPayoutValue(row.staff_id, "extra_pay")),
          notes: getPayoutValue(row.staff_id, "notes"),
        }));
        const saved = await createPayrollRun(
          {
            start: previewData.start_date || startDate,
            end: previewData.end_date || endDate,
            staff_payouts: staffPayouts,
          },
          token
        );
        setPreviewData(saved);
        setCurrentRunId(saved.id);
        await loadRuns();
      } else {
        const updates = (previewData.staff || []).map((row) =>
          updatePayrollRunStaffPayout(
            currentRunId,
            row.staff_id,
            {
              staff_id: row.staff_id,
              pay_cheque: toNumber(getPayoutValue(row.staff_id, "pay_cheque")),
              pay_cash: toNumber(getPayoutValue(row.staff_id, "pay_cash")),
              extra_pay: toNumber(getPayoutValue(row.staff_id, "extra_pay")),
              notes: getPayoutValue(row.staff_id, "notes"),
            },
            token
          )
        );
        await Promise.all(updates);
        const refreshed = await getPayrollRun(currentRunId, token);
        setPreviewData(refreshed);
      }
      setNotice("Payroll run saved.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save payroll run.");
    } finally {
      setSavingRun(false);
    }
  };

  const handleExport = async () => {
    if (!token) return;
    if (!currentRunId) {
      setError("Save the payroll run before exporting.");
      return;
    }
    try {
      const res = await exportPayrollRunCsv(currentRunId, token);
      const disposition = res?.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^\"]+)"?/i);
      const filename = match?.[1] || "payroll.csv";
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to export payroll.");
    }
  };

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const jumped = scrollToSection(id);
      if (!jumped) return;
      setHighlightedSectionId(id);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedSectionId((current) => (current === id ? "" : current));
      }, 1500);
    },
    [scrollToSection]
  );

  const getSectionClassName = useCallback(
    (id, baseClassName) =>
      `${baseClassName} ${
        highlightedSectionId === id
          ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
          : ""
      }`,
    [highlightedSectionId]
  );

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/hr/training");
  };

  return (
    <div className="hrSectionShell">
      <div className={`hrSectionWide hr-section-compact ${modalText}`}>
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">Payroll</h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div id="pr-overview">
              <ReportHelpCard
                title="Help Guide: Payroll"
                subtitle="Run payroll preview, set rules, and save payroll runs."
                sections={HELP_SECTIONS}
                troubleshooting={TROUBLESHOOTING_ITEMS}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            {notice && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {notice}
              </div>
            )}
            {settingsLoading && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
                Loading payroll settings...
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_0.7fr]">
              <div
                id="pr-range"
                className={getSectionClassName(
                  "pr-range",
                  "rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4"
                )}
              >
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">Payroll Range</h2>
                  <p className="text-sm text-slate-500">
                    Select a date range in the salon timezone to preview payroll.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <label className="text-sm text-slate-600">
                    <span className="inline-flex items-center gap-2">
                      Start Date
                      <InfoTooltip text={TOOLTIPS.start_date} label="Start Date" />
                    </span>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="text-sm text-slate-600">
                    <span className="inline-flex items-center gap-2">
                      End Date
                      <InfoTooltip text={TOOLTIPS.end_date} label="End Date" />
                    </span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </label>
                  <div className="flex flex-col justify-end gap-2">
                    <button
                      type="button"
                      onClick={handlePreview}
                      disabled={previewLoading}
                      className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                    >
                      {previewLoading ? "Loading..." : "Preview Payroll"}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveRun}
                      disabled={!previewData || savingRun}
                      className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                    >
                      {savingRun ? "Saving..." : "Save Payroll Run"}
                    </button>
                    <button
                      type="button"
                      onClick={handleExport}
                      disabled={!currentRunId}
                      className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      Export CSV
                    </button>
                  </div>
                </div>
                {dateError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">
                    {dateError}
                  </div>
                )}
              </div>

              <div
                id="pr-history"
                className={getSectionClassName(
                  "pr-history",
                  "rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3"
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2">
                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                      Payroll History
                    </h2>
                    <InfoTooltip text={TOOLTIPS.payroll_history} label="Payroll History" />
                  </div>
                  {runsLoading && (
                    <span className="text-xs text-slate-500">Loading...</span>
                  )}
                </div>
                <div className="space-y-2 max-h-60 overflow-auto pr-1">
                  {payrollRuns.length === 0 && !runsLoading && (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                      No payroll runs saved yet.
                    </div>
                  )}
                  {payrollRuns.map((run) => {
                    const label = `${formatDateOnlyYYYYMMDD(run.start_date)} - ${formatDateOnlyYYYYMMDD(run.end_date)}`;
                    const isActive = run.id === currentRunId;
                    return (
                      <div
                        key={run.id}
                        className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-xs transition ${
                          isActive
                            ? "border-slate-300 bg-slate-50 text-slate-700"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleLoadRun(run.id)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="font-semibold">{label}</div>
                          <div className="text-[11px] opacity-80">
                            {run.created_by_name || "Saved"} ? {run.created_at ? new Date(run.created_at).toLocaleDateString() : ""}
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setConfirmDeleteRun(run);
                            setConfirmDeleteOpen(true);
                          }}
                          disabled={deletingRunId === run.id}
                          className="rounded-full border border-slate-200 px-3 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-60"
                        >
                          {deletingRunId === run.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div
              id="pr-commission"
              className={getSectionClassName(
                "pr-commission",
                "rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4"
              )}
            >
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">Commission & Bonus Rules</h2>
                <p className="text-sm text-slate-500">
                  Define tiered commissions for service, retail, or total revenue.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <label className="text-sm text-slate-600">
                  <span className="inline-flex items-center gap-2">
                    Label
                    <InfoTooltip text={TOOLTIPS.label} label="Label" />
                  </span>
                  <input
                    type="text"
                    value={commissionLabel}
                    onChange={(e) => setCommissionLabel(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-sm text-slate-600">
                  <span className="inline-flex items-center gap-2">
                    Basis
                    <InfoTooltip text={TOOLTIPS.basis} label="Basis" />
                  </span>
                  <select
                    value={defaultBasis}
                    onChange={(e) => setDefaultBasis(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="service">Service</option>
                    <option value="retail">Retail</option>
                    <option value="total">Total</option>
                  </select>
                </label>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                  >
                    Save Settings
                  </button>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left">Min Revenue</th>
                      <th className="px-3 py-2 text-left">Rate %</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {defaultTiers.map((tier, idx) => (
                      <tr key={`default-tier-${idx}`}>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={tier.min_revenue}
                            onChange={(e) => updateDefaultTier(idx, "min_revenue", e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.001"
                            value={tier.rate_percent}
                            onChange={(e) => updateDefaultTier(idx, "rate_percent", e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeDefaultTier(idx)}
                            className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                    {defaultTiers.length === 0 && (
                      <tr>
                        <td className="px-3 py-3 text-slate-500" colSpan={3}>
                          Add commission tiers to begin.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="border-t border-slate-200 bg-slate-50 px-3 py-2">
                  <button
                    type="button"
                    onClick={addDefaultTier}
                    className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    Add Tier
                  </button>
                </div>
              </div>
            </div>

            <div
              id="pr-rates"
              className={getSectionClassName("pr-rates", "grid grid-cols-1 gap-6 lg:grid-cols-2")}
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">Hourly Rate Levels</h2>
                  <p className="text-sm text-slate-500">Define hourly pay levels.</p>
                </div>
                <div className="rounded-2xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                      <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Amount</th>
                        <th className="px-3 py-2 text-left">Sort</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {hourlyLevels.map((level) => (
                        <tr key={level.id}>
                          <td className="px-3 py-2">
                            <input
                              value={level.name}
                              onChange={(e) => updateRateLevelField(level.id, "name", e.target.value)}
                              className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.01"
                              value={level.amount}
                              onChange={(e) => updateRateLevelField(level.id, "amount", e.target.value)}
                              className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={level.sort_order}
                              onChange={(e) => updateRateLevelField(level.id, "sort_order", e.target.value)}
                              className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2 text-right space-x-2">
                            <button
                              type="button"
                              onClick={() => handleUpdateRateLevel(level)}
                              className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteRateLevel(level.id)}
                              className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td className="px-3 py-2">
                          <input
                            value={newHourlyLevel.name}
                            onChange={(e) => setNewHourlyLevel((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder="Level name"
                            className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={newHourlyLevel.amount}
                            onChange={(e) => setNewHourlyLevel((prev) => ({ ...prev, amount: e.target.value }))}
                            placeholder="0.00"
                            className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={newHourlyLevel.sort_order}
                            onChange={(e) => setNewHourlyLevel((prev) => ({ ...prev, sort_order: e.target.value }))}
                            placeholder="0"
                            className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleCreateRateLevel("hourly")}
                            className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                          >
                            Add
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">Daily Rate Levels</h2>
                  <p className="text-sm text-slate-500">Define daily pay levels.</p>
                </div>
                <div className="rounded-2xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                      <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Amount</th>
                        <th className="px-3 py-2 text-left">Sort</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {dailyLevels.map((level) => (
                        <tr key={level.id}>
                          <td className="px-3 py-2">
                            <input
                              value={level.name}
                              onChange={(e) => updateRateLevelField(level.id, "name", e.target.value)}
                              className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.01"
                              value={level.amount}
                              onChange={(e) => updateRateLevelField(level.id, "amount", e.target.value)}
                              className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={level.sort_order}
                              onChange={(e) => updateRateLevelField(level.id, "sort_order", e.target.value)}
                              className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2 text-right space-x-2">
                            <button
                              type="button"
                              onClick={() => handleUpdateRateLevel(level)}
                              className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteRateLevel(level.id)}
                              className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td className="px-3 py-2">
                          <input
                            value={newDailyLevel.name}
                            onChange={(e) => setNewDailyLevel((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder="Level name"
                            className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={newDailyLevel.amount}
                            onChange={(e) => setNewDailyLevel((prev) => ({ ...prev, amount: e.target.value }))}
                            placeholder="0.00"
                            className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={newDailyLevel.sort_order}
                            onChange={(e) => setNewDailyLevel((prev) => ({ ...prev, sort_order: e.target.value }))}
                            placeholder="0"
                            className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleCreateRateLevel("daily")}
                            className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                          >
                            Add
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div
              id="pr-staff"
              className={getSectionClassName(
                "pr-staff",
                "rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4"
              )}
            >
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">Employee Pay Setup</h2>
                <p className="text-sm text-slate-500">
                  Assign pay modes, levels, and overrides for each staff member.
                </p>
              </div>
              {!settingsLoading && staffProfiles.length === 0 && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  <div className="font-semibold text-slate-700">No time clock employees</div>
                  <div>
                    To add employees to payroll, go to Manage &gt; Staff and enable
                    {" "}
                    'Uses Time Clock' for the employee.
                  </div>
                </div>
              )}
              <div className="rounded-2xl border border-slate-200 overflow-x-auto">
                <table className={STAFF_SETUP_TABLE_CLASS}>
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className={`${STAFF_SETUP_HEADER_CELL_CLASS} w-[160px] min-w-[160px]`}>
                        Employee
                      </th>
                      <th className={`${STAFF_SETUP_HEADER_CELL_CLASS} w-[150px] min-w-[150px]`}>
                        <span className="inline-flex items-center gap-2">
                          Pay Mode
                          <InfoTooltip text={TOOLTIPS.pay_mode} label="Pay Mode" />
                        </span>
                      </th>
                      <th className={`${STAFF_SETUP_HEADER_CELL_CLASS} w-[180px] min-w-[180px]`}>
                        Hourly Level
                      </th>
                      <th className={`${STAFF_SETUP_HEADER_CELL_CLASS} w-[170px] min-w-[170px]`}>
                        Hourly Override
                      </th>
                      <th className={`${STAFF_SETUP_HEADER_CELL_CLASS} w-[180px] min-w-[180px]`}>
                        Daily Level
                      </th>
                      <th className={`${STAFF_SETUP_HEADER_CELL_CLASS} w-[170px] min-w-[170px]`}>
                        Daily Override
                      </th>
                      <th className={`${STAFF_SETUP_HEADER_CELL_CLASS} w-[180px] min-w-[180px]`}>
                        Custom Commission
                      </th>
                      <th className="px-3 py-2 text-right whitespace-nowrap w-[96px] min-w-[96px]">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {staffProfiles.map((profile) => {
                      const staffBasis = staffBasisById[profile.staff_id] || defaultBasis;
                      const staffTiers = staffPlansByStaff?.[profile.staff_id]?.[staffBasis] || [];
                      const showCustomTierNotice = profile.use_custom_commission && staffTiers.length === 0;
                      return (
                        <Fragment key={profile.staff_id}>
                        <tr key={profile.staff_id}>
                          <td className="px-3 py-2 font-semibold text-slate-900 whitespace-nowrap">
                            {profile.staff_name}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={profile.pay_mode}
                              onChange={(e) =>
                                updateStaffProfileField(profile.staff_id, "pay_mode", e.target.value)
                              }
                              className={STAFF_SETUP_SELECT_CLASS}
                            >
                              <option value="commission">Commission</option>
                              <option value="hourly">Hourly</option>
                              <option value="daily">Daily</option>
                              <option value="hybrid">Hybrid</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={profile.hourly_level_id || ""}
                              onChange={(e) =>
                                updateStaffProfileField(
                                  profile.staff_id,
                                  "hourly_level_id",
                                  e.target.value ? Number(e.target.value) : null
                                )
                              }
                              className={STAFF_SETUP_LEVEL_SELECT_CLASS}
                            >
                              <option value="">None</option>
                              {hourlyLevels.map((level) => (
                                <option key={level.id} value={level.id}>
                                  {level.name} (${formatMoney(level.amount)})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <div className="relative group">
                              <input
                                type="number"
                                step="0.01"
                                value={profile.hourly_rate_override ?? ""}
                                onChange={(e) =>
                                  updateStaffProfileField(
                                    profile.staff_id,
                                    "hourly_rate_override",
                                    e.target.value
                                  )
                                }
                                className={STAFF_SETUP_OVERRIDE_INPUT_CLASS}
                              />
                              <span
                                tabIndex={0}
                                aria-label="Hourly override tip"
                                className="absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-[10px] font-semibold text-slate-500 focus:outline-none focus-visible:ring focus-visible:ring-slate-300"
                              >
                                i
                              </span>
                              <div className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-64 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] text-slate-700 shadow-md group-hover:block group-focus-within:block">
                                Tip: Entering 0 will override the hourly rate to $0.00.
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={profile.daily_level_id || ""}
                              onChange={(e) =>
                                updateStaffProfileField(
                                  profile.staff_id,
                                  "daily_level_id",
                                  e.target.value ? Number(e.target.value) : null
                                )
                              }
                              className={STAFF_SETUP_LEVEL_SELECT_CLASS}
                            >
                              <option value="">None</option>
                              {dailyLevels.map((level) => (
                                <option key={level.id} value={level.id}>
                                  {level.name} (${formatMoney(level.amount)})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <div className="relative group">
                              <input
                                type="number"
                                step="0.01"
                                value={profile.daily_rate_override ?? ""}
                                onChange={(e) =>
                                  updateStaffProfileField(
                                    profile.staff_id,
                                    "daily_rate_override",
                                    e.target.value
                                  )
                                }
                                className={STAFF_SETUP_OVERRIDE_INPUT_CLASS}
                              />
                              <span
                                tabIndex={0}
                                aria-label="Daily override tip"
                                className="absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-[10px] font-semibold text-slate-500 focus:outline-none focus-visible:ring focus-visible:ring-slate-300"
                              >
                                i
                              </span>
                              <div className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-64 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] text-slate-700 shadow-md group-hover:block group-focus-within:block">
                                Tip: Entering 0 will override the daily rate to $0.00.
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <label className="inline-flex items-center gap-2 whitespace-nowrap text-xs text-slate-600">
                              <input
                                type="checkbox"
                                checked={Boolean(profile.use_custom_commission)}
                                onChange={(e) =>
                                  updateStaffProfileField(
                                    profile.staff_id,
                                    "use_custom_commission",
                                    e.target.checked
                                  )
                                }
                                className="h-4 w-4 rounded border-slate-300"
                              />
                              Custom tiers
                            </label>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handleSaveStaffProfile(profile)}
                              className="whitespace-nowrap text-xs font-semibold text-slate-700 hover:text-slate-900"
                            >
                              Save
                            </button>
                          </td>
                        </tr>
                        {profile.use_custom_commission && (
                          <tr>
                            <td className="px-3 py-3 bg-slate-50" colSpan={8}>
                              <div className="flex flex-col gap-3">
                                <div className="flex flex-wrap items-center gap-3">
                                  <label className="text-xs text-slate-600">
                                    Basis
                                    <select
                                      value={staffBasis}
                                      onChange={(e) =>
                                        setStaffBasisById((prev) => ({
                                          ...prev,
                                          [profile.staff_id]: e.target.value,
                                        }))
                                      }
                                      className="ml-2 rounded-lg border border-slate-200 px-2 py-1 pr-7 text-xs"
                                    >
                                      <option value="service">Service</option>
                                      <option value="retail">Retail</option>
                                      <option value="total">Total</option>
                                    </select>
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => handleSaveStaffCommissionPlan(profile.staff_id)}
                                    className="whitespace-nowrap rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800"
                                  >
                                    Save Custom Tiers
                                  </button>
                                </div>
                                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                                  <table className="w-full text-xs">
                                    <thead className="bg-slate-100 text-slate-500 uppercase tracking-wide">
                                      <tr>
                                        <th className="px-3 py-2 text-left">Min Revenue</th>
                                        <th className="px-3 py-2 text-left">Rate %</th>
                                        <th className="px-3 py-2 text-right">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                      {staffTiers.map(
                                        (tier, idx) => (
                                          <tr key={`staff-${profile.staff_id}-tier-${idx}`}>
                                            <td className="px-3 py-2">
                                              <input
                                                type="number"
                                                step="0.01"
                                                value={tier.min_revenue}
                                                onChange={(e) =>
                                                  updateStaffTier(profile.staff_id, idx, "min_revenue", e.target.value)
                                                }
                                                className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                                              />
                                            </td>
                                            <td className="px-3 py-2">
                                              <input
                                                type="number"
                                                step="0.001"
                                                value={tier.rate_percent}
                                                onChange={(e) =>
                                                  updateStaffTier(profile.staff_id, idx, "rate_percent", e.target.value)
                                                }
                                                className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                                              />
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                              <button
                                                type="button"
                                                onClick={() => removeStaffTier(profile.staff_id, idx)}
                                                className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                                              >
                                                Remove
                                              </button>
                                            </td>
                                          </tr>
                                        )
                                      )}
                                      {staffTiers.length === 0 && (
                                        <tr>
                                          <td className="px-3 py-3 text-slate-500" colSpan={3}>
                                            Add tiers for this staff member.
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                  <div className="border-t border-slate-200 bg-slate-50 px-3 py-2">
                                    <button
                                      type="button"
                                      onClick={() => addStaffTier(profile.staff_id)}
                                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                                    >
                                      Add Tier
                                    </button>
                                  </div>
                                </div>
                                {showCustomTierNotice && (
                                  <p className="text-[11px] text-slate-500">
                                    No custom tiers set - global bonus rules will be used until you add tiers.
                                  </p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  </tbody>
                </table>
              </div>
            </div>

            {previewData && (
              <div id="pr-results" className={getSectionClassName("pr-results", "space-y-6 rounded-2xl")}>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Payroll Results
                      </h2>
                      <p className="text-sm text-slate-500">
                        {formatDateOnlyYYYYMMDD(previewData.start_date)} to {formatDateOnlyYYYYMMDD(previewData.end_date)} ? {previewData.salon_timezone}
                      </p>
                    </div>
                    {currentRunId && (
                      <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                        Saved Run
                      </div>
                    )}
                  </div>
                  <div className="rounded-2xl border border-slate-200 overflow-x-auto">
                    <table className="w-full min-w-[980px] text-[13px]">
                      <thead className="bg-slate-50 text-[12px] font-semibold text-slate-500 uppercase tracking-wide">
                        <tr>
                          <th className="px-3 py-2 text-left w-[140px] whitespace-nowrap">Employee</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[56px]">Hours</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">HR Rate</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[56px]">Days</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Day Rate</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Service</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Retail</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Revenue</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[84px]">{activeCommissionLabel}</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Base Pay</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Total Pay</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {(previewData.staff || []).map((row) => (
                          <tr key={`summary-${row.staff_id}`}>
                            <td className="px-3 py-2 font-semibold text-slate-900 w-[140px] whitespace-nowrap">
                              <span
                                className="block max-w-[140px] truncate"
                                title={row.staff_name}
                              >
                                {row.staff_name}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-right whitespace-nowrap w-[56px]">{formatMoney(row.hours)}</td>
                            <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(row.hourly_rate)}</td>
                            <td className="px-2 py-2 text-right whitespace-nowrap w-[56px]">{formatMoney(row.days)}</td>
                            <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(row.daily_rate)}</td>
                            <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(row.service_sales)}</td>
                            <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(row.retail_sales)}</td>
                            <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(row.total_sales)}</td>
                            <td className="px-2 py-2 text-right whitespace-nowrap w-[84px]">${formatMoney(row.commission_pay)}</td>
                            <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(row.base_pay)}</td>
                            <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(row.total_pay)}</td>
                          </tr>
                        ))}
                        <tr className="bg-slate-50 font-semibold">
                          <td className="px-3 py-2 w-[140px] whitespace-nowrap">Totals</td>
                          <td className="px-2 py-2 text-right whitespace-nowrap w-[56px]">{formatMoney(totalSummary.hours)}</td>
                          <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">--</td>
                          <td className="px-2 py-2 text-right whitespace-nowrap w-[56px]">{formatMoney(totalSummary.days)}</td>
                          <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">--</td>
                          <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(totalSummary.service)}</td>
                          <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(totalSummary.retail)}</td>
                          <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(totalSummary.totalSales)}</td>
                          <td className="px-2 py-2 text-right whitespace-nowrap w-[84px]">${formatMoney(totalSummary.commission)}</td>
                          <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(totalSummary.base)}</td>
                          <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(totalSummary.totalPay)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                  <div>
                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                      Daily Breakdown
                    </h2>
                    <p className="text-sm text-slate-500">
                      {""}
                      Daily values show base pay when daily rates apply, otherwise service sales.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 overflow-x-auto">
                    <table className="w-full min-w-[1080px] text-[13px]">
                      <thead className="bg-slate-50 text-[12px] font-semibold text-slate-500 uppercase tracking-wide">
                        <tr>
                          <th className="px-3 py-2 text-left w-[140px] whitespace-nowrap">Employee</th>
                          {dayKeys.map((day) => (
                            <th key={`day-${day}`} className="px-2 py-2 text-right whitespace-nowrap w-[78px]">
                              <div className="text-[12px] font-semibold">{formatDateOnlyYYYYMMDD(day)}</div>
                              <div className="text-[11px] text-slate-400">{formatWeekday(day)}</div>
                            </th>
                          ))}
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Total</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[56px]">Days</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Pay</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[84px]">{activeCommissionLabel}</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Extra</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {(previewData.staff || []).map((row) => {
                          const usesDaily = ["daily", "hybrid"].includes(row.pay_mode);
                          const extraPay = toNumber(getPayoutValue(row.staff_id, "extra_pay"));
                          const totalPay = toNumber(row.base_pay) + toNumber(row.commission_pay) + extraPay;
                          const detail = breakdownDetailsByStaff[row.staff_id] || {};
                          const detailByDate = detail.by_date || {};
                          const detailTotals = detail.totals || {};
                          return (
                            <Fragment key={`breakdown-${row.staff_id}`}>
                              <tr>
                                <td className="px-3 py-2 font-semibold text-slate-900 w-[140px] whitespace-nowrap">
                                  <span
                                    className="block max-w-[140px] truncate"
                                    title={row.staff_name}
                                  >
                                    {row.staff_name}
                                  </span>
                                </td>
                                {dayKeys.map((day) => {
                                  const dayData = previewData.daily_breakdown?.[row.staff_id]?.[day] || {};
                                  const displayValue = usesDaily
                                    ? dayData.base_pay
                                    : dayData.service_sales;
                                  return (
                                    <td
                                      key={`cell-${row.staff_id}-${day}`}
                                      className="px-2 py-2 text-right whitespace-nowrap w-[78px]"
                                    >
                                      ${formatMoney(displayValue)}
                                    </td>
                                  );
                                })}
                                <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(row.total_sales)}</td>
                                <td className="px-2 py-2 text-right whitespace-nowrap w-[56px]">{formatMoney(row.days)}</td>
                                <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(row.base_pay)}</td>
                                <td className="px-2 py-2 text-right whitespace-nowrap w-[84px]">${formatMoney(row.commission_pay)}</td>
                                <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(extraPay)}</td>
                                <td className="px-2 py-2 text-right whitespace-nowrap w-[72px]">${formatMoney(totalPay)}</td>
                              </tr>
                              <tr className="bg-slate-50 text-[12px] text-slate-600">
                                <td className="px-3 py-1.5 pl-6 font-medium w-[140px] whitespace-nowrap">Service</td>
                                {dayKeys.map((day) => {
                                  const dayDetail = detailByDate[day] || {};
                                  return (
                                    <td
                                      key={`service-${row.staff_id}-${day}`}
                                      className="px-2 py-1.5 text-right whitespace-nowrap w-[78px]"
                                    >
                                      ${formatMoney(dayDetail.service_net)}
                                    </td>
                                  );
                                })}
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">${formatMoney(detailTotals.service_net)}</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[56px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[84px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">--</td>
                              </tr>
                              <tr className="bg-slate-50 text-[12px] text-slate-600">
                                <td className="px-3 py-1.5 pl-6 font-medium w-[140px] whitespace-nowrap">Retail</td>
                                {dayKeys.map((day) => {
                                  const dayDetail = detailByDate[day] || {};
                                  return (
                                    <td
                                      key={`retail-${row.staff_id}-${day}`}
                                      className="px-2 py-1.5 text-right whitespace-nowrap w-[78px]"
                                    >
                                      ${formatMoney(dayDetail.retail_net)}
                                    </td>
                                  );
                                })}
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">${formatMoney(detailTotals.retail_net)}</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[56px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[84px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">--</td>
                              </tr>
                              <tr className="bg-slate-50 text-[12px] text-slate-600">
                                <td className="px-3 py-1.5 pl-6 font-medium w-[140px] whitespace-nowrap">Bonus</td>
                                {dayKeys.map((day) => {
                                  const dayDetail = detailByDate[day] || {};
                                  return (
                                    <td
                                      key={`bonus-${row.staff_id}-${day}`}
                                      className="px-2 py-1.5 text-right whitespace-nowrap w-[78px]"
                                    >
                                      ${formatMoney(dayDetail.bonus)}
                                    </td>
                                  );
                                })}
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">${formatMoney(detailTotals.bonus)}</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[56px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[84px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">--</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap w-[72px]">--</td>
                              </tr>
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                  <div>
                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                      Pay Split
                    </h2>
                    <p className="text-sm text-slate-500">
                      Split payments between cheque and cash, and add extras.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 overflow-x-auto">
                    <table className="w-full min-w-[860px] text-[13px]">
                      <thead className="bg-slate-50 text-[12px] font-semibold text-slate-500 uppercase tracking-wide">
                        <tr>
                          <th className="px-3 py-2 text-left w-[140px] whitespace-nowrap">Employee</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[96px]">Pay Cheque</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Cash</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[72px]">Extra</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[96px]">Actual Cash</th>
                          <th className="px-3 py-2 text-left min-w-[160px]">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {(previewData.staff || []).map((row) => {
                          const payCash = getPayoutValue(row.staff_id, "pay_cash");
                          return (
                            <tr key={`payout-${row.staff_id}`}>
                              <td className="px-3 py-2 font-semibold text-slate-900 w-[140px] whitespace-nowrap">
                                <span
                                  className="block max-w-[140px] truncate"
                                  title={row.staff_name}
                                >
                                  {row.staff_name}
                                </span>
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={getPayoutValue(row.staff_id, "pay_cheque")}
                                  onChange={(e) => setPayoutValue(row.staff_id, "pay_cheque", e.target.value)}
                                  className="w-full rounded-lg border border-slate-200 px-2 py-1 text-[13px] text-right"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={payCash}
                                  onChange={(e) => setPayoutValue(row.staff_id, "pay_cash", e.target.value)}
                                  className="w-full rounded-lg border border-slate-200 px-2 py-1 text-[13px] text-right"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={getPayoutValue(row.staff_id, "extra_pay")}
                                  onChange={(e) => setPayoutValue(row.staff_id, "extra_pay", e.target.value)}
                                  className="w-full rounded-lg border border-slate-200 px-2 py-1 text-[13px] text-right"
                                />
                              </td>
                              <td className="px-2 py-2 text-right font-semibold whitespace-nowrap">
                                ${formatMoney(payCash)}
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  value={getPayoutValue(row.staff_id, "notes")}
                                  onChange={(e) => setPayoutValue(row.staff_id, "notes", e.target.value)}
                                  className="w-full rounded-lg border border-slate-200 px-2 py-1 text-[13px]"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={confirmDeleteOpen}
        onClose={() => {
          setConfirmDeleteOpen(false);
          setConfirmDeleteRun(null);
        }}
        onConfirm={handleDeleteRun}
        title="Confirm"
        message="Delete this payroll run? This cannot be undone."
        cancelLabel="Cancel"
        confirmLabel="Delete"
        confirmingLabel="Deleting..."
        isSubmitting={deletingRunId === confirmDeleteRun?.id}
      />
    </div>
  );
}
